import React from 'react';
import {Avatar, Box, Tooltip, Typography} from '@mui/material';
import {
  GridColDef,
  GridRenderCellParams,
  GridTreeNodeWithRender,
} from '@mui/x-data-grid-pro';
import {Link} from 'react-router-dom';
import moment from 'moment';
import {renderCellDate} from '../../utils/renderCellDate';
import {ReportPackStatusColourScheme} from '../ReportPackPage/ReportPackHelpers';
import {ColoredChip} from '../../components/EditorGrid/GridRenderComponents';
import {
  SearchReportPacks_searchReportPacks,
  SearchReportPacks_searchReportPacks_contacts,
} from '../../schema-types/SearchReportPacks';
import {themeColors} from '../../theme/Theme';
import {getDaysAgo} from '../../utils/getDaysAgo';

export type ReportPackDashboardColumnsProps =
  GridColDef<SearchReportPacks_searchReportPacks> & {
    field:
      | keyof SearchReportPacks_searchReportPacks
      | 'accountName'
      | 'reviewName'
      | 'adviserName'
      | 'firstContactName'
      | 'secondContactName';
  };

const getChaserNumber = (title?: string) => {
  const lowercaseTitle = title?.toLocaleLowerCase();
  if (lowercaseTitle === '1 week reminder') {
    return '1';
  }

  if (lowercaseTitle === '2 week reminder') {
    return '2';
  }

  return '3';
};

const getChaserColour = (title?: string) => {
  const lowercaseTitle = title?.toLocaleLowerCase();
  if (lowercaseTitle === '1 week reminder') {
    return themeColors.yellow;
  }

  if (lowercaseTitle === '2 week reminder') {
    return themeColors.coral;
  }

  return themeColors.red;
};

const chaserValueGetter = (
  params: GridRenderCellParams<
    SearchReportPacks_searchReportPacks,
    any,
    any,
    GridTreeNodeWithRender
  >,
  index: number
) => {
  const {contacts} = params.row;
  const chasers = contacts?.[index]?.chasers ?? [];

  if (!chasers.length) {
    return null;
  }

  return chasers.map((chaser) => {
    return (
      <Tooltip
        key={chaser?.id}
        title={
          <span>
            <b>{chaser?.title}</b>
            {': '}
            {`${getDaysAgo(new Date(), chaser?.dateCreated)} days ago`}
          </span>
        }
      >
        <Avatar sx={{bgcolor: getChaserColour(chaser?.title)}}>
          {getChaserNumber(chaser?.title)}
        </Avatar>
      </Tooltip>
    );
  });
};

export const reportPackDashboardColumns: ReportPackDashboardColumnsProps[] = [
  {
    field: 'accountName',
    sortable: true,
    headerName: 'Account Name',
    flex: 0.8,
    renderCell(params) {
      return <Typography>{params.row.review?.account?.name ?? 'N/A'}</Typography>;
    },
    sortComparator: (v1, v2, param1, param2) => {
      const row1 = param1.api.getRow(param1.id);
      const row2 = param2.api.getRow(param2.id);
      const name1 = row1?.review?.account?.name ?? '';
      const name2 = row2?.review?.account?.name ?? '';
      console.log('AccountName sortComparator:', name1, 'vs', name2);
      return name1.localeCompare(name2);
    },
  },
  {
    field: 'reviewName',
    sortable: true,
    headerName: 'Review Name',
    flex: 0.8,
    renderCell(params) {
      return <Typography>{params.row?.review?.name ?? 'N/A'}</Typography>;
    },
    sortComparator: (v1, v2, param1, param2) => {
      const row1 = param1.api.getRow(param1.id);
      const row2 = param2.api.getRow(param2.id);
      const name1 = row1?.review?.name ?? '';
      const name2 = row2?.review?.name ?? '';
      console.log('ReviewName sortComparator:', name1, 'vs', name2);
      return name1.localeCompare(name2);
    },
  },
  {
    field: 'adviserName',
    sortable: true,
    headerName: 'Adviser',
    flex: 0.7,
    align: 'center',
    headerAlign: 'center',
    renderCell(params) {
      return <Typography>{params.row.review?.adviser?.name ?? 'N/A'}</Typography>;
    },
    sortComparator: (v1, v2, param1, param2) => {
      const row1 = param1.api.getRow(param1.id);
      const row2 = param2.api.getRow(param2.id);
      const name1 = row1?.review?.adviser?.name ?? '';
      const name2 = row2?.review?.adviser?.name ?? '';
      return name1.localeCompare(name2);
    },
  },
  {
    field: 'status',
    sortable: true,
    headerName: 'Status',
    flex: 0.5,
    align: 'center',
    headerAlign: 'center',
    renderCell(params) {
      return (
        <ColoredChip colorScheme={ReportPackStatusColourScheme} value={params.value} />
      );
    },
  },
  {
    field: 'submittedDate',
    sortable: true,
    headerName: 'Submitted Date',
    flex: 0.8,
    align: 'center',
    headerAlign: 'center',
    renderCell: renderCellDate,
  },
  {
    field: 'firstContactName',
    sortable: true,
    headerName: 'First Contact',
    flex: 0.7,
    align: 'center',
    headerAlign: 'center',
    renderCell(params) {
      const contact = params.row.contacts.find((c) =>
        c?.fullName?.includes(String(params.row.firstContact?.firstName))
      );
      return (
        <RenderContact contact={contact} firstName={params.row.firstContact.firstName} />
      );
    },
    sortComparator: (v1, v2, param1, param2) => {
      const row1 = param1.api.getRow(param1.id);
      const row2 = param2.api.getRow(param2.id);
      const name1 = row1?.firstContact?.firstName ?? '';
      const name2 = row2?.firstContact?.firstName ?? '';
      console.log('FirstContactName sortComparator:', name1, 'vs', name2);
      return name1.localeCompare(name2);
    },
  },
  {
    field: 'numberOfForms', // using this field to display the chasers
    sortable: false,
    headerName: 'Reminders',
    flex: 0.7,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => {
      // zero here means the first contact :(
      return chaserValueGetter(params, 0);
    },
  },
  {
    field: 'secondContactName',
    sortable: true,
    headerName: 'Second Contact',
    flex: 0.7,
    align: 'center',
    headerAlign: 'center',
    renderCell(params) {
      if (typeof params.row.secondContact?.firstName !== 'string') {
        return <>N/A</>;
      }
      const contact = params.row.contacts.find((c) =>
        c?.fullName?.includes(String(params.row.secondContact?.firstName))
      );
      return (
        <RenderContact
          contact={contact}
          firstName={params.row.secondContact?.firstName}
        />
      );
    },
    sortComparator: (v1, v2, param1, param2) => {
      const row1 = param1.api.getRow(param1.id);
      const row2 = param2.api.getRow(param2.id);
      const name1 = row1?.secondContact?.firstName ?? '';
      const name2 = row2?.secondContact?.firstName ?? '';
      console.log('SecondContactName sortComparator:', name1, 'vs', name2);
      return name1.localeCompare(name2);
    },
  },
  {
    field: 'contacts', // using this field to display the chasers
    sortable: false,
    headerName: 'Reminders',
    flex: 0.7,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => {
      // one here means the second contact :(
      return chaserValueGetter(params, 1);
    },
  },
  {
    field: 'allFormsCompleted', // this is where the view button will live
    sortable: false,
    headerName: '',
    flex: 0.5,
    align: 'center',
    renderCell(params) {
      return (
        <Link
          role="a"
          to={{
            pathname: `../account/${params.row.review?.account?.id}/review-journey/${params.row.review?.id}/report`,
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
          style={{
            fontWeight: 600,
            backgroundColor: themeColors.coral,
            textDecoration: 'none',
            cursor: 'pointer',
            borderRadius: '5px',
            color: themeColors.white,
            maxHeight: '50px',
            minWidth: '50px',
            lineHeight: '2rem',
          }}
        >
          View
        </Link>
      );
    },
  },
];

const RenderContact: React.FC<{
  contact: SearchReportPacks_searchReportPacks_contacts | null | undefined;
  firstName: string | null | undefined;
}> = ({contact, firstName}) => {
  return (
    <Box display="flex" flexDirection="column" alignItems="center">
      <Typography>{firstName ?? 'N/A'}</Typography>
      <ColoredChip
        colorScheme={{
          Incomplete: themeColors.lightYellow,
          Complete: themeColors.lightGreen,
        }}
        value={contact?.signedStatus ? 'Complete' : 'Incomplete'}
      />
      {contact?.signedStatus && (
        <Typography>
          {moment(contact.dateSigned?.valueOf()).format('DD/MM/YY')}
        </Typography>
      )}
    </Box>
  );
};
